import React, { useRef, useState } from "react";
import styles from "shared/components/inputs/Input.module.scss";

interface PriceInputProps {
  value: number;
  onChange: (value: number) => void;
  customClass?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  customClass,
  error,
  placeholder = "R$ 0,00",
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selecionado, setSelecionado] = useState(false);

  // O value do formulário/API está sempre em CENTAVOS.
  const centavosAtuais = Math.round(Number(value) || 0);

  const formatarValor = (centavos: number): string => {
    return (centavos / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const valorFormatado =
    centavosAtuais > 0
      ? `R$ ${formatarValor(centavosAtuais)}`
      : "";

  const adicionarDigito = (digito: string) => {
    const novosCentavos = selecionado
      ? Number(digito)
      : centavosAtuais * 10 + Number(digito);

    onChange(novosCentavos);
    setSelecionado(false);
  };

  const removerUltimoDigito = () => {
    if (selecionado) {
      onChange(0);
      setSelecionado(false);
      return;
    }

    const novosCentavos = Math.floor(centavosAtuais / 10);

    onChange(novosCentavos);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (disabled) {
      return;
    }

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      adicionarDigito(event.key);
      return;
    }

    if (
      event.key === "Backspace" ||
      event.key === "Delete"
    ) {
      event.preventDefault();
      removerUltimoDigito();
      return;
    }

    if (
      event.key === "Tab" ||
      event.key === "Enter" ||
      event.key === "Escape"
    ) {
      return;
    }

    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "Home" ||
      event.key === "End"
    ) {
      event.preventDefault();
      return;
    }

    if (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === "a"
    ) {
      event.preventDefault();

      inputRef.current?.select();
      setSelecionado(true);
      return;
    }

    if (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === "c"
    ) {
      return;
    }

    if (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === "v"
    ) {
      return;
    }

    event.preventDefault();
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    const textoColado = event.clipboardData
      .getData("text")
      .replace(/\D/g, "");

    if (!textoColado) {
      onChange(0);
      return;
    }

    // O texto colado representa centavos.
    // Ex.: "30000" -> R$ 300,00
    const novosCentavos = Number(textoColado);

    onChange(novosCentavos);
    setSelecionado(false);
  };

  const handleFocus = () => {
    inputRef.current?.select();
    setSelecionado(true);
  };

  const handleBlur = () => {
    setSelecionado(false);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={valorFormatado}
        placeholder={placeholder}
        disabled={disabled}
        readOnly
        className={`${styles.inputs} ${customClass ?? ""}`}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label="Valor em reais"
      />

      {error && (
        <p className={styles.error}>
          {error}
        </p>
      )}
    </>
  );
};